import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  getInventorySearchAPI,
  getInventoryFilterAPI,
  getInventorySmartFilterCountAPI,
} from "../Home-Page/home-page.component.service";
import { addToCart } from "../../reducers/app.reducer";
import { getProductPrice } from "../../utils/cart.utils";
import { useDispatch, useSelector } from "react-redux";
import { saveCartItemsAPI } from "../../service/cart.service";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

const ProductSearchDetail = () => {
  const { partNumber } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryCounts, setCategoryCounts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.app);
  const { cart } = useSelector((state) => state.app);
  const MySwal = withReactContent(Swal);
  const [page, setPage] = useState(1);
  const limit = 10; // items per page
  const skip = (page - 1) * limit;
  const [totalCount, setTotalCount] = useState(0);
  const [selectedSmartFilters, setSelectedSmartFilters] = useState([]);
  const [specFilters, setSpecFilters] = useState([]);
  // Smart filters are category-scoped. The filter API keys on the category's
  // UUID (category_unique_id), which each search result carries — the numeric
  // category_id returns empty spec_values.
  const filterCategory = products?.[0]?.category_unique_id || null;

  const onLoad = async () => {
    try {
      setLoading(true);
      const result = await getInventorySearchAPI({
        keyword: partNumber,
        categoryId: selectedCategory,
        uniqueId: filterCategory,
        filters: selectedSmartFilters,
        skip,
        limit,
      });
      console.log({ result });
      if (result.status) {
        const productsData = result.data.result || result.data;
        setProducts(productsData);
        setCategoryCounts(result.data.category_match_counts?.result || []);
        setTotalCount(result.data.count);
      } else {
        setError("No products found matching your search");
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("Failed to load product details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1); 
  }, [partNumber, selectedCategory]);

  useEffect(() => {
    if (partNumber) {
      onLoad();
    }
  }, [partNumber, selectedCategory, page, selectedSmartFilters]);

  // Load smart-filter facets for the category in context (mirrors Inventory page)
  const loadSmartFilters = async () => {
    if (!filterCategory) {
      setSpecFilters([]);
      return;
    }
    try {
      const apiFilter = await getInventoryFilterAPI(filterCategory);
      if (apiFilter.status && apiFilter.data) {
        const countRes = await getInventorySmartFilterCountAPI({
          uniqueId: filterCategory,
          filters: selectedSmartFilters,
        });
        const specData = apiFilter.data;
        const updatedFilters = [];
        for (let i = 0; i < (specData.spec_values?.length || 0); i++) {
          const filteredValue = countRes.data?.find(
            (spec) => spec.spec_id === specData.spec_values[i].specification_id
          );
          updatedFilters.push({
            ...specData.spec_values[i],
            values: filteredValue?.values || [],
          });
        }
        setSpecFilters({ ...apiFilter.data, spec_values: updatedFilters });
      }
    } catch (err) {
      console.error("Failed to load smart filters:", err);
    }
  };

  useEffect(() => {
    loadSmartFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategory, selectedSmartFilters]);

  const handleFilterChange = (specId, specName, event) => {
    const specValue = event.target.value;
    const copy = [...selectedSmartFilters];
    const idx = copy.findIndex((f) => f.spec_id === specId);
    if (idx >= 0) copy[idx] = { ...copy[idx], spec_value: specValue };
    else copy.push({ spec_id: specId, spec_value: specValue });
    setSelectedSmartFilters(copy);
    setPage(1);
  };

  const handleApplyFilter = () => {
    setPage(1);
    onLoad();
  };

  const handleResetAll = () => {
    setSelectedSmartFilters([]);
    setPage(1);
  };

  const handleRemoveChip = (specId) => {
    setSelectedSmartFilters((prev) => prev.filter((f) => f.spec_id !== specId));
    const selectBox = document.querySelector(`select[data-specid="${specId}"]`);
    if (selectBox) {
      Array.from(selectBox.options).forEach((opt) => (opt.selected = false));
    }
    setPage(1);
  };

  const handleKeywordCategoryMatch = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const shouldShowCategorySelector =
    categoryCounts?.length > 1 && !selectedCategory;

  const shouldShowProducts = !shouldShowCategorySelector;

  const totalPages = Math.ceil(totalCount / limit);

  const getVisiblePages = () => {
    const pages = [];

    const start = Math.max(1, page - 4);
    const end = Math.min(totalPages, page + 4);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  const handleAddToCart = async (product) => {
    // Check if user is logged in
    if (!user) {
      // Redirect to login page with a return URL
      navigate("/login", { state: { from: window.location.pathname } });
      return;
    }

    // Create a clean version of cart data with only necessary fields
    console.log({ cart });
    const cartData = cart.map((item) => {
      console.log({ item });
      const selected_price = getProductPrice(item);
      return {
        product_id: item.id,
        part_number: item.part_number,
        name: item.name,
        description: item.description,
        brand: item.brand,
        model: item.model,
        category: item.category,
        sub_category: item.sub_category,
        price: item.price,
        image: item.image,
        required_qty: item.required_qty || 1,
        alternate_packaging: [],
        selected_price: selected_price,
        actual_price: selected_price,
        total_amount: selected_price * (item.required_qty || 1),
      };
    });

    try {
      // Save cart item to database
      const response = await saveCartItemsAPI({
        cart: cartData,
        customer_id: user.id,
      });

      if (response.status) {
        console.log("Item added to cart successfully", response.data);
        // Optionally update the local cart state if needed
      } else {
        console.error("Failed to add item to cart:", response.message);
      }
    } catch (error) {
      console.error("Error adding item to cart:", error);
    }

    // Normalize price object if it's an array
    let priceObj = product.price;

    if (Array.isArray(product.price)) {
      const sortedSlabs = [...product.price].sort(
        (a, b) => parseInt(a.slab) - parseInt(b.slab)
      );

      priceObj = {
        min_order: parseInt(sortedSlabs[0]?.slab || 1),
        multiple: parseInt(sortedSlabs[0]?.slab || 1),
        price_list: sortedSlabs.map((slab) => ({
          min_qty: parseInt(slab.slab),
          price: parseFloat(slab.price),
        })),
      };
    }

    let requiredQty = product.required_qty || priceObj.min_order;
    let multiple = priceObj.multiple || 1;

    // Check if required quantity is greater than available quantity
    if (requiredQty > product.available_qty) {
      // Use partner's min and multiple quantities
      const partnerMinQty = parseInt(product.partner_min_qty) || 1;
      const partnerMulQty = parseInt(product.partner_mul_qty) || 1;

      // Calculate the minimum valid quantity based on partner's requirements
      const minValidQty = Math.max(
        partnerMinQty,
        Math.ceil(requiredQty / partnerMulQty) * partnerMulQty
      );

      // Update required quantity and multiple
      requiredQty = minValidQty;
      multiple = partnerMulQty;

      // Show a message to the user
      MySwal.fire({
        title: <strong>Quantity Adjusted</strong>,
        html: (
          <i>
            Your requested quantity exceeds available stock. Adjusted to{" "}
            {requiredQty} based on partner's minimum ({partnerMinQty}) and
            multiple ({partnerMulQty}) quantities.
          </i>
        ),
        icon: "info",
      });
    }
    // Validate quantity is a multiple of the defined multiple
    else if (requiredQty % multiple !== 0) {
      MySwal.fire({
        title: <strong>Invalid Quantity</strong>,
        html: <i>Please enter a quantity in multiples of {multiple}.</i>,
        icon: "warning",
      });
      return;
    }

    const cartProduct = {
      ...product,
      required_qty: requiredQty,
      manufacturer: product.make,
      price: priceObj,
    };

    console.log({ product, cartProduct });

    MySwal.fire({
      title: <strong>Cart Update!</strong>,
      html: <i>You've added {product.part_number} to your cart </i>,
      icon: "success",
    });

    dispatch(addToCart(cartProduct));
  };

  const handleClickPartNumber = (partNumber) => {
    // console.log({ partNumber });
    navigate(`/product/${partNumber}`);
  };

  if (loading) {
    return (
      <div className="container text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading products...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="container py-5 text-center">
        <h3>No Products Found</h3>
        <p>No products found matching: {partNumber}</p>
      </div>
    );
  }
  console.log({ products });

  return (
    <div className="ds-container" style={{ paddingBlock: 24 }}>
      <div className="ds-page-head">
        <p className="ds-eyebrow">Search results</p>
        <div className="ds-head-row">
          <h1 style={{ fontSize: 26 }}>Results for “{partNumber}”</h1>
          <span className="ds-muted">{totalCount} parts found</span>
        </div>
      </div>
      {shouldShowCategorySelector && !loading && (
        <div className="d-flex justify-content-center">
          <div
            className="table-responsive"
            style={{ maxWidth: "600px", width: "100%" }}
          >
            <table className="table table-bordered text-center">
              <thead className="table-light">
                <tr>
                  <th>Category</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {categoryCounts?.map((item) => (
                  <tr key={item.category_id}>
                    <td>
                      <a
                        className="btn btn-link p-0"
                        onClick={() => {
                          handleKeywordCategoryMatch(item.category_id);
                        }}
                      >
                        {item.category_name || item.category_id}
                      </a>
                    </td>
                    <td>{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {shouldShowProducts &&
        (specFilters?.make?.length > 0 || specFilters?.spec_values?.length > 0) && (
          <>
            <div className="ds-filter-bar" style={{ marginBottom: 12, alignItems: "flex-start", gap: 12 }}>
              {specFilters?.make?.length > 0 && (
                <div>
                  <label className="ds-eyebrow" style={{ display: "block", marginBottom: 6 }}>Manufacturer</label>
                  <select
                    className="form-select"
                    multiple
                    size={6}
                    value={selectedSmartFilters.filter((f) => f.spec_id === "make").map((f) => f.spec_value)}
                    data-specid="make"
                    onChange={(e) => handleFilterChange("make", "make", e)}
                    style={{ minWidth: 160 }}
                  >
                    {specFilters.make.map((make, i) => (
                      <option key={i} value={make.make}>{make.make}</option>
                    ))}
                  </select>
                </div>
              )}
              {specFilters?.spec_values?.map((spec, i) => (
                <div key={i}>
                  <label className="ds-eyebrow" style={{ display: "block", marginBottom: 6 }}>{spec.specification_name}</label>
                  <select
                    className="form-select"
                    multiple
                    size={6}
                    value={selectedSmartFilters
                      .filter((f) => f.spec_id === spec.specification_id.toString())
                      .map((f) => f.spec_value)}
                    onChange={(e) => handleFilterChange(spec.specification_id.toString(), spec.specification_name, e)}
                    style={{ minWidth: 160 }}
                  >
                    {spec.values && spec.values.map((v, vi) => (
                      <option key={`${spec.specification_id}-${vi}`} value={v.value}>
                        {v.value} ({v.count})
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <div className="ds-row ds-between" style={{ marginBottom: 16 }}>
              <div className="ds-row">
                {selectedSmartFilters?.length > 0 && <span className="ds-muted">Applied:</span>}
                {selectedSmartFilters?.map((filter, i) => (
                  <span key={i} className="ds-chip on">
                    {filter.spec_value}
                    <span className="x" role="button" aria-label="Remove" onClick={() => handleRemoveChip(filter.spec_id)}>×</span>
                  </span>
                ))}
              </div>
              <div className="ds-row">
                <button className="ds-btn ds-btn-ghost ds-btn-sm" onClick={handleResetAll}>Reset all</button>
                <button className="ds-btn ds-btn-primary ds-btn-sm" onClick={handleApplyFilter}>Apply filter</button>
              </div>
            </div>
          </>
        )}

      {shouldShowProducts && (
        <div className="ds-table-wrap">
          <table className="ds-table zebra">
            <thead className="">
              <tr>
                <th>Image</th>
                <th>Part Number</th>
                <th>Manufacturer</th>
                <th>Description</th>
                {/* <th>Category</th> */}
                <th>Available Qty</th>
                <th
                  style={{
                    textAlign: "center",
                  }}
                >
                  Buy
                </th>
                <th
                  style={{
                    textAlign: "center",
                  }}
                >
                  Price
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(products) && products.length > 0 ? (
                products.map(
                  (item, index) => (
                    console.log({ item }),
                    (
                      <tr key={item.id || index}>
                        <td style={{ border: "1px solid #ddd" }}>
                          <img
                            src={item.image || "N/A"}
                            alt=""
                            style={{ width: "70px", height: "70px" }}
                          />
                        </td>
                        <td
                          className="part-number"
                          onClick={() =>
                            handleClickPartNumber(item.part_number)
                          }
                        >
                          {item.part_number || "N/A"}
                        </td>
                        <td style={{ border: "1px solid #ddd" }}>
                          {item.make || "N/A"}
                        </td>
                        <td className="description">
                          {item.description || "N/A"}
                        </td>
                        <td style={{ border: "1px solid #ddd" }}>
                          {item.available_qty || "N/A"}
                        </td>
                        <td style={{ border: "1px solid #ddd" }}>
                          <div className="quantity-input">
                            <input
                              type="text"
                              // value={item.quantity || ""}
                              ref={(el) => (item.qtyRef = el)}
                            />
                            <button
                              type="button"
                              className="btn-buy"
                              onClick={() => {
                                const enteredQty =
                                  parseInt(item.qtyRef?.value) || 0;
                                handleAddToCart({
                                  ...item,
                                  required_qty: enteredQty,
                                });
                              }}
                            >
                              <span>Buy</span>
                            </button>
                          </div>
                        </td>
                        <td style={{ border: "1px solid #ddd" }}>
                          {item.price && item.price.length > 0
                            ? item.price.map((priceItem, index) => (
                                <div key={index} className="price-range">
                                  <div className="price-item">
                                    <span className="quantity">
                                      {priceItem.slab}
                                    </span>
                                    <span className="price">
                                      : ₹{" "}
                                      {parseFloat(priceItem.price).toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              ))
                            : "0.00"}
                        </td>
                        <td style={{ border: "1px solid #ddd" }}>
                          <Link
                            to={`/product/${item.part_number}`}
                            className="btn-view"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    )
                  )
                )
              ) : (
                <tr>
                  <td colSpan="6" className="no-data">
                    No inventory items found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {shouldShowProducts && totalPages > 1 && (
        <div
          className="pagination"
          style={{
            display: "flex",
            justifyContent: "center",
            margin: "20px 0",
            // gap: '5px',
            flexWrap: "wrap",
          }}
        >
          {/* Pagination buttons */}
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage(1)}>
                  First
                </button>
              </li>

              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage(page - 1)}>
                  Previous
                </button>
              </li>

              {getVisiblePages().map((p) => (
                <li
                  key={p}
                  className={`page-item ${p === page ? "active" : ""}`}
                >
                  <button className="page-link" onClick={() => setPage(p)}>
                    {p}
                  </button>
                </li>
              ))}

              <li
                className={`page-item ${page === totalPages ? "disabled" : ""}`}
              >
                <button className="page-link" onClick={() => setPage(page + 1)}>
                  Next
                </button>
              </li>

              <li
                className={`page-item ${page === totalPages ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setPage(totalPages)}
                >
                  Last
                </button>
              </li>
            </ul>
          </nav>

          <div
            style={{
              marginLeft: "15px",
              display: "flex",
              alignItems: "center",
              fontSize: "14px",
              color: "#666",
              marginTop: "30px",
            }}
          >
            Page {page} of {totalPages}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSearchDetail;
